import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auctionService } from "@/services";
import type { Auction } from "@/services/types";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { EmptyState } from "@/components/base/EmptyState";
import { ErrorState } from "@/components/base/ErrorState";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { Select, type SelectOption } from "@/components/base/Select";
import { formatDate } from "@/utils/format";
import { isSameDay } from "@/utils/auction";

function startOfToday(): Date {
  const t = new Date();
  return new Date(t.getFullYear(), t.getMonth(), t.getDate());
}

interface Grouped {
  dueToday: Auction[];
  overdue: Auction[];
  upcoming: Auction[];
  closed: Auction[];
}

function groupAuctions(auctions: Auction[]): Grouped {
  const today = startOfToday();
  const g: Grouped = { dueToday: [], overdue: [], upcoming: [], closed: [] };
  for (const a of auctions) {
    if (a.status === "closed") {
      g.closed.push(a);
      continue;
    }
    const d = new Date(a.scheduledDate);
    if (Number.isNaN(d.getTime())) {
      g.upcoming.push(a);
      continue;
    }
    if (isSameDay(d, today)) g.dueToday.push(a);
    else if (d < today) g.overdue.push(a);
    else g.upcoming.push(a);
  }
  return g;
}

function AuctionEntry({
  auction,
  onOpen,
}: {
  auction: Auction;
  onOpen: (auction: Auction) => void;
}) {
  const isOverdue = auction.status === "pending" && new Date(auction.scheduledDate) < startOfToday() && !isSameDay(auction.scheduledDate, startOfToday());
  const actionLabel = auction.status === "closed"
    ? "View record"
    : auction.isOrganizerReserved
    ? "Complete cycle"
    : "Open console";

  return (
    <div
      className={`flex flex-col gap-3 rounded-lg border bg-background-50 p-4 sm:flex-row sm:items-center sm:justify-between ${
        isOverdue ? "border-danger-200" : "border-background-200"
      }`}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <div
          className={`flex h-10 w-10 flex-none items-center justify-center rounded-md ${
            isOverdue ? "bg-danger-100 text-danger-600" : "bg-secondary-100 text-secondary-700"
          }`}
        >
          <i className={`${isOverdue ? "ri-alarm-warning-line" : "ri-hammer-line"} text-lg`} />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-foreground-900">{auction.groupName}</span>
            <span className="tnum text-xs text-foreground-400">{auction.groupCode}</span>
            {auction.isOrganizerReserved ? (
              <Badge color="accent" dot>Reserved cycle</Badge>
            ) : (
              <Badge variant="active" dot>Cycle {auction.cycle}</Badge>
            )}
            {isOverdue ? <Badge variant="overdue">Overdue</Badge> : null}
          </div>
          <p className="mt-0.5 text-sm text-foreground-600">
            <span className="tnum font-medium">{auction.periodLabel}</span>
            <span className="mx-2 text-foreground-300">·</span>
            Auction {formatDate(auction.scheduledDate)}
            <span className="mx-2 text-foreground-300">·</span>
            {auction.isOrganizerReserved
              ? `Organizer: ${auction.organizer?.fullName ?? "—"}`
              : `${auction.eligible.length} member${auction.eligible.length === 1 ? "" : "s"} eligible to bid`}
          </p>
        </div>
      </div>
      <div className="flex flex-none items-center justify-between gap-4 sm:justify-end">
        <div className="text-right">
          <p className="text-xs text-foreground-500">Pool</p>
          <p className="tnum text-base font-semibold text-foreground-900">
            <CurrencyDisplay amount={auction.pool} hideSymbol />
          </p>
        </div>
        <Button size="sm" variant={isOverdue ? "danger" : "primary"} icon={<i className="ri-arrow-right-s-line" />} onClick={() => onOpen(auction)}>
          {actionLabel}
        </Button>
      </div>
    </div>
  );
}

function Section({
  title,
  tone,
  count,
  auctions,
  onOpen,
  empty,
}: {
  title: string;
  tone: "default" | "danger" | "accent" | "secondary";
  count: number;
  auctions: Auction[];
  onOpen: (a: Auction) => void;
  empty: string;
}) {
  const dot =
    tone === "danger"
      ? "bg-danger-500"
      : tone === "accent"
      ? "bg-accent-500"
      : tone === "secondary"
      ? "bg-secondary-500"
      : "bg-primary-500";
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${dot}`} />
        <h3 className="text-sm font-semibold text-foreground-800">{title}</h3>
        <span className="tnum rounded-full bg-background-100 px-2 py-0.5 text-xs text-foreground-500">{count}</span>
      </div>
      {auctions.length === 0 ? (
        <p className="rounded-lg border border-dashed border-background-200 px-4 py-4 text-sm text-foreground-400">{empty}</p>
      ) : (
        <div className="space-y-2">
          {auctions.map((a) => (
            <AuctionEntry key={a.id} auction={a} onOpen={onOpen} />
          ))}
        </div>
      )}
    </div>
  );
}

function getCurrentMonthKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export default function AuctionPage() {
  const navigate = useNavigate();
  const [auctions, setAuctions] = useState<Auction[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [monthFilter, setMonthFilter] = useState<string>("");

  const load = useCallback(async () => {
    setError(null);
    try {
      setAuctions(await auctionService.listAuctions());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load auctions.");
      setAuctions([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const open = useCallback((a: Auction) => navigate(`/auction/${a.groupId}/${a.cycle}`), [navigate]);

  const monthOptions: SelectOption[] = useMemo(() => {
    if (!auctions) return [];
    const map = new Map<string, string>();
    for (const a of auctions) {
      if (!a.scheduledDate) continue;
      const d = new Date(a.scheduledDate);
      if (Number.isNaN(d.getTime())) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
      map.set(key, label);
    }
    const sortedKeys = Array.from(map.keys()).sort();
    return [
      { value: "", label: "Current & past months" },
      { value: "all", label: "All months (incl. future)" },
      ...sortedKeys.map((key) => ({ value: key, label: map.get(key)! })),
    ];
  }, [auctions]);

  const visible = useMemo(() => {
    if (!auctions) return [];
    const currentMonthKey = getCurrentMonthKey();
    if (!monthFilter) {
      // Default: show current & past months only, exclude future months
      return auctions.filter((a) => {
        if (!a.scheduledDate) return true;
        const d = new Date(a.scheduledDate);
        if (Number.isNaN(d.getTime())) return true;
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        return key <= currentMonthKey;
      });
    }
    if (monthFilter === "all") return auctions;
    return auctions.filter((a) => {
      if (!a.scheduledDate) return false;
      const d = new Date(a.scheduledDate);
      if (Number.isNaN(d.getTime())) return false;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      return key === monthFilter;
    });
  }, [auctions, monthFilter]);

  const grouped = useMemo(() => groupAuctions(visible), [visible]);
  const dueTodayCount = useMemo(() => {
    if (!auctions) return 0;
    const today = startOfToday();
    return auctions.filter((a) => a.status === "pending" && isSameDay(a.scheduledDate, today)).length;
  }, [auctions]);

  if (error && !auctions) {
    return (
      <div>
        <PageHeader breadcrumb={<><BreadcrumbItem label="Home" to="/" /><BreadcrumbItem label="Auction" isLast /></>} title="Auction console" />
        <Card><ErrorState message={error} onRetry={load} /></Card>
      </div>
    );
  }

  const hasAny = grouped.dueToday.length + grouped.overdue.length + grouped.upcoming.length + grouped.closed.length > 0;

  return (
    <div>
      <PageHeader
        breadcrumb={
          <>
            <BreadcrumbItem label="Home" to="/" />
            <BreadcrumbItem label="Auction" isLast />
          </>
        }
        title="Auction console"
        subtitle={`${dueTodayCount} auction${dueTodayCount === 1 ? "" : "s"} due today`}
        actions={
          <div className="flex items-center gap-2">
            <div className="w-56">
              <Select
                value={monthFilter}
                onChange={setMonthFilter}
                options={monthOptions}
                placeholder="Current & past months"
              />
            </div>
            {monthFilter ? (
              <Button size="sm" variant="outline" onClick={() => setMonthFilter("")}>
                Reset filter
              </Button>
            ) : null}
          </div>
        }
      />

      {error ? <div className="mb-3"><ErrorState message={error} onRetry={load} /></div> : null}

      {!auctions ? (
        <div className="space-y-3">
          <div className="h-24 animate-pulse rounded-lg bg-background-200" />
          <div className="h-24 animate-pulse rounded-lg bg-background-200" />
          <div className="h-24 animate-pulse rounded-lg bg-background-200" />
        </div>
      ) : !hasAny ? (
        <Card>
          <EmptyState
            icon="ri-hammer-line"
            title="No auctions to show"
            description={
              monthFilter
                ? `No auctions scheduled for the selected month. Clear the month filter to see everything.`
                : "There are no pending or closed auctions right now."
            }
          />
        </Card>
      ) : (
        <div className="space-y-6">
          <Section
            title="Due today"
            tone="default"
            count={grouped.dueToday.length}
            auctions={grouped.dueToday}
            onOpen={open}
            empty="Nothing due today. Nice and clear."
          />
          <Section
            title="Overdue — action required"
            tone="danger"
            count={grouped.overdue.length}
            auctions={grouped.overdue}
            onOpen={open}
            empty="No overdue auctions. Every month's collection is clear."
          />
          <Section
            title="Upcoming auctions"
            tone="secondary"
            count={grouped.upcoming.length}
            auctions={grouped.upcoming}
            onOpen={open}
            empty="Nothing scheduled yet this month."
          />
          <Section
            title="Recently closed"
            tone="accent"
            count={grouped.closed.length}
            auctions={grouped.closed}
            onOpen={open}
            empty="No closed records."
          />
        </div>
      )}
    </div>
  );
}